/**
 * A small, dependency-free Markdown → HTML renderer for blog post bodies (ported from the legacy
 * app's renderMarkdown.js).
 *
 * Security: all source text is HTML-escaped before any formatting is applied, link hrefs are
 * restricted to http/https/mailto, and image sources to http(s) URLs and same-origin paths. No
 * author-supplied HTML reaches the DOM, so the output is safe for dangerouslySetInnerHTML.
 * Supports headings, bold, italic, inline code, links, images, lists, blockquotes, fenced code,
 * horizontal rules and paragraphs.
 */
const escapeHtml = (str: string) =>
  str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const sanitizeHref = (href: string) => {
  const trimmed = href.trim();
  return /^(https?:|mailto:)/i.test(trimmed) ? trimmed : "#";
};

const sanitizeSrc = (src: string) => {
  const trimmed = src.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^\/[^/]/.test(trimmed)) return trimmed; // same-origin absolute path
  return "";
};

/** Inline formatting, applied to already-escaped text. */
const renderInline = (text: string) => {
  let out = text;
  out = out.replace(/`([^`]+)`/g, (_m, code: string) => `<code>${code}</code>`);
  out = out.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, alt: string, src: string) => {
    const safe = sanitizeSrc(src);
    return safe ? `<img src="${safe}" alt="${alt}" loading="lazy" />` : "";
  });
  out = out.replace(
    /\[([^\]]+)\]\(([^)\s]+)\)/g,
    (_m, label: string, href: string) =>
      `<a href="${sanitizeHref(href)}" target="_blank" rel="noopener noreferrer">${label}</a>`,
  );
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  return out;
};

// The source is escaped before parsing, so a quote marker arrives as "&gt;". (The legacy renderer
// matched a literal ">", so its blockquotes never rendered.)
const QUOTE = /^&gt;\s?/;

const SPECIAL = [/^(#{1,4})\s+/, QUOTE, /^\s*[-*]\s+/, /^\s*\d+\.\s+/];

export function renderMarkdown(markdown: string | null | undefined): string {
  if (!markdown) return "";
  const lines = escapeHtml(markdown).replace(/\r\n/g, "\n").split("\n");
  const html: string[] = [];
  let i = 0;
  let listType: "ul" | "ol" | null = null;
  const closeList = () => {
    if (listType) {
      html.push(`</${listType}>`);
      listType = null;
    }
  };
  const openList = (type: "ul" | "ol") => {
    if (listType !== type) {
      closeList();
      html.push(`<${type}>`);
      listType = type;
    }
  };

  while (i < lines.length) {
    const line = lines[i];

    if (/^```/.test(line.trim())) {
      closeList();
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) code.push(lines[i++]);
      i++; // closing fence
      html.push(`<pre><code>${code.join("\n")}</code></pre>`);
      continue;
    }
    if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      closeList();
      html.push("<hr />");
      i++;
      continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      closeList();
      const level = heading[1].length;
      html.push(`<h${level}>${renderInline(heading[2].trim())}</h${level}>`);
      i++;
      continue;
    }
    if (QUOTE.test(line)) {
      closeList();
      const quote: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i])) quote.push(lines[i++].replace(QUOTE, ""));
      html.push(`<blockquote>${renderInline(quote.join(" "))}</blockquote>`);
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      openList("ul");
      html.push(`<li>${renderInline(line.replace(/^\s*[-*]\s+/, ""))}</li>`);
      i++;
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      openList("ol");
      html.push(`<li>${renderInline(line.replace(/^\s*\d+\.\s+/, ""))}</li>`);
      i++;
      continue;
    }
    if (line.trim() === "") {
      closeList();
      i++;
      continue;
    }
    closeList();
    const para = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !SPECIAL.some((re) => re.test(lines[i])) &&
      !/^```/.test(lines[i].trim())
    ) {
      para.push(lines[i++]);
    }
    html.push(`<p>${renderInline(para.join(" "))}</p>`);
  }
  closeList();
  return html.join("\n");
}
